import { createBlock } from 'factories/block';
import { InteractionProps, repeat } from './interaction';
import { createLayout } from 'factories/layout';
import { findBlocksIn } from 'selectors/block';
import { Block, cloneBlock, Blocks } from 'factories/block';
import { flatten } from 'factories/utils';
import { colors } from 'styles/variables';
import { createContinueBtn } from './continueBtn';
import { getSId } from '../utils';
import { createMediaBlock } from './media';
import placeholder from 'assets/addImg@3x.svg';
import { getPlaceholderText } from './text';

const { lGrey0 } = colors;

export enum ImageChoice {
  Left = 'Left',
  Right = 'Right',
  Fill = 'Fill',
  None = 'None',
}

export type AccordionInteractionProps = InteractionProps & {
  numberOfAccordions: number;
  imgChoice: ImageChoice;
};

const makeLeftChoice = (imgBlock: Block, textBlock: Block): Block[] => {
  return [
    createBlock('Column', [imgBlock]),
    createBlock('Column', [textBlock]),
  ];
};

const makeRightChoice = (imgBlock: Block, textBlock: Block): Block[] => {
  return [
    createBlock('Column', [textBlock]),
    createBlock('Column', [imgBlock]),
  ];
};

const makeFillChoice = (imgBlock: Block, textBlock: Block): Block[] => {
  return [createBlock('Column', [imgBlock, textBlock])];
};

const makeNoneChoice = (textBlock: Block): Block[] => {
  return [createBlock('Column', [textBlock])];
};

const createChoice = (choice: ImageChoice = ImageChoice.None): Block[] => {
  const media = [
    createBlock('Image'),
    createMediaBlock({
      child: 1,
      type: 'Accordion_Media',
      removeTypes: ['Embed'],
    }),
  ][1];

  const createTextBlock = createBlock('Text', [], {
    props: { content: 'Description text example' },
  });

  switch (choice) {
    case ImageChoice.Left: {
      return makeLeftChoice(media, createTextBlock);
    }
    case ImageChoice.Right: {
      return makeRightChoice(media, createTextBlock);
    }
    case ImageChoice.Fill: {
      return makeFillChoice(media, createTextBlock);
    }
    case ImageChoice.None: {
      return makeNoneChoice(createTextBlock);
    }
  }
};

const collectLeftChoice = (b: Block, blocks: Blocks): Block[] => {
  const bs = b.children as Block[];

  if (bs.length === 1) {
    // if this is the case then we got broken somewhere
    // return the content with no image. repeating for right and fill layouts also
    return collectNoneChoice(b, blocks);
  }

  const bs0 = (blocks[bs[0].id].children as Block[])[0];
  const bs1 = (blocks[bs[1].id].children as Block[])[0];

  return [bs0, bs1];
};

const collectRightChoice = (b: Block, blocks: Blocks): Block[] => {
  const bs = b.children as Block[];

  if (bs.length === 1) {
    return collectNoneChoice(b, blocks);
  }

  const bs0 = (blocks[bs[0].id].children as Block[])[0];
  const bs1 = (blocks[bs[1].id].children as Block[])[0];
  return [bs1, bs0];
};

const collectFillChoice = (b: Block, blocks: Blocks): Block[] => {
  const bs = b.children as Block[];
  const bs0 = (blocks[bs[0].id].children as Block[])[0];
  const bs1 = (blocks[bs[0].id].children as Block[])[1];

  return [bs0, bs1];
};

const collectNoneChoice = (b: Block, blocks: Blocks): Block[] => {
  const bs = b.children as Block[];
  const bs0 = (blocks[bs[0].id].children as Block[])[0];

  return [
    createMediaBlock({
      child: 1,
      type: 'Accordion_Media',
      src: placeholder,
      removeTypes: ['Embed'],
    }),
    bs0,
  ];
};

const collectFromChoice = (
  choice: ImageChoice,
  b: Block,
  blocks: Blocks
): Block[] => {
  switch (choice) {
    case ImageChoice.Left: {
      return collectLeftChoice(b, blocks);
    }
    case ImageChoice.Right: {
      return collectRightChoice(b, blocks);
    }
    case ImageChoice.Fill: {
      return collectFillChoice(b, blocks);
    }
    case ImageChoice.None: {
      return collectNoneChoice(b, blocks);
    }
  }
};

export const createAccordionFieldBlock = (img: ImageChoice) => (): Block => {
  return createBlock('AccordionField', [
    createBlock('AccordionHeader', [
      createBlock('Text', [], { props: { content: 'Example text' } }),
    ]),
    createBlock('AccordionBody', [...createChoice(img)], {
      meta: { imageChoice: img },
    }),
  ]);
};

export const createAccordionBlock = (
  count: number,
  img: ImageChoice
): Block => {
  const createAFB = createAccordionFieldBlock(img);
  return createBlock('Accordion', [...repeat(createAFB, count)]);
};

export const removeAccordionField = (block: Block, position: number): Block => {
  if (block.type !== 'Accordion') {
    const nextBlock = cloneBlock(block);
    (nextBlock.children as Block[]).splice(position, 1);
    return nextBlock;
  }

  return block;
};

export const addAccordionField = (block: Block, position: number): Block => {
  if (block.type !== 'Accordion') {
    const nextBlock = cloneBlock(block);
    (nextBlock.children as Block[]).splice(
      position,
      0,
      createAccordionFieldBlock(ImageChoice.None)()
    );
    return nextBlock;
  }
  return block;
};

// TODO: Test
export const changeImageChoice = (blocks: Blocks) => (
  block: Block,
  imgChoice: ImageChoice
): [Block, Blocks] => {
  if (block.type !== 'Interaction' || block.meta!.subType !== 'Accordion') {
    throw new Error('Must pass an accordion interaction');
  }

  const newIBlock = cloneBlock(block);
  const abBlocks = findBlocksIn(
    blocks,
    newIBlock,
    r => r.type === 'AccordionBody'
  );

  const newBlocks = abBlocks
    .map(
      (b: Block): Blocks => {
        const newBlock = cloneBlock(b);
        const meta = block.meta as any;

        const current = collectFromChoice(
          meta.imageChoice || 'Left',
          newBlock,
          blocks
        );

        const currentText =
          blocks[current[1].id].type === 'Text' ? current[1] : current[0];
        const currentImage =
          blocks[current[1].id].type === 'Text' ? current[0] : current[1];

        let newChoice;

        switch (imgChoice) {
          case ImageChoice.Left: {
            newChoice = [
              createBlock('Column', [currentImage]),
              createBlock('Column', [currentText]),
            ];
            break;
          }
          case ImageChoice.Right: {
            newChoice = [
              createBlock('Column', [currentText]),
              createBlock('Column', [currentImage]),
            ];
            break;
          }
          case ImageChoice.Fill: {
            newChoice = [createBlock('Column', [currentImage, currentText])];
            break;
          }
          case ImageChoice.None: {
            newChoice = [createBlock('Column', [currentText])];
            break;
          }
        }

        newBlock.children = newChoice;

        const [newBlocks] = flatten(newBlock);

        return newBlocks;
      }
    )
    .reduce((acc, c) => ({ ...acc, ...c }), {} as Blocks);

  newIBlock.meta = { ...newIBlock.meta, imageChoice: imgChoice } as any;

  return [newIBlock, newBlocks];
};

const contentForColumn = (count: number, imgChoice: ImageChoice) => (
  i: number
) => {
  return [
    () =>
      createMediaBlock({
        child: 0,
        textPlaceholderContent: getPlaceholderText('h1', 'heading-one'),
        className: 'text-align-center',
      }),
    () =>
      createBlock('InteractionColumn', [
        createAccordionBlock(count, imgChoice),
        createMediaBlock({
          child: 0,
          isAdditionalMedia: true,
        }),
      ]),
  ][i];
};

export const removeAccordionPanel = (
  block: Block,
  position: number
): [Block, Blocks?] => {
  if (block.type === 'Accordion') {
    const nextBlock = cloneBlock(block);
    (nextBlock.children as Block[]).splice(position, 1);

    return [nextBlock];
  }
  return [block, undefined];
};

export const addAccordionPanel = (
  block: Block,
  position: number,
  choice: ImageChoice
): [Block, Blocks?] => {
  if (block.type === 'Accordion') {
    const accordionPanelBlock = createAccordionFieldBlock(choice)();
    const accordionPanelBlockRef = { id: accordionPanelBlock.id, type: 'Ref' };
    const [newBlocks] = flatten(accordionPanelBlock);

    const nextBlock = cloneBlock(block);
    (nextBlock.children as Block[]).splice(position, 0, accordionPanelBlockRef);

    return [nextBlock, newBlocks];
  }
  return [block, undefined];
};

export const createAccordionInteractionBlock = (
  props: AccordionInteractionProps
) => {
  const {
    // headingTextColor = '#1A1A2B',
    // headingColor = '#FBFBFD',
    // fieldBodyTextColor,
    // fieldBodyBackgroundColor = '#fff',
    lockDown = 0,
    style,
  } = props as any;

  let columnCount = 0; //  props.columnCount;
  let columnsReversed = true; //  props.columnsReversed;

  return createBlock(
    'Interaction',
    [
      createLayout(
        contentForColumn(props.numberOfAccordions, props.imgChoice),
        columnCount,
        columnsReversed
      ),
      createBlock('SingleColumnLayout', [
        createBlock('Column', [createContinueBtn(style)], {
          props: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
          },
          meta: {
            subType: 'ButtonBlock',
          },
        }),
      ]),
    ],
    {
      props: {
        name: props.name,
        sId: getSId('interaction'),
        style: props.style,
        lockDown,
      },
      meta: {
        subType: 'Accordion',
        layout: `${columnCount === 0 ? 'OneColumn' : 'TwoColumns'}${
          columnsReversed ? 'Reversed' : ''
        }`,
        imageChoice: props.imgChoice,
      },
    }
  );
};

export const interactionConfig = {};
